/**
 * RED metrics (Rate / Errors / Duration) — in-memory counters, Prometheus exposition.
 * SSOT: docs/inflomatrix-constitution/ops/SLO.md, ALERT_THRESHOLDS.md
 * No prom-client; flush via metrics-exporters.ts (logger / OTLP HTTP / Sentry breadcrumb).
 */

import { maskPiiInString } from './pii';

export const DURATION_BUCKET_UPPER_SEC = [
  0.005,
  0.01,
  0.025,
  0.05,
  0.1,
  0.25,
  0.5,
  1,
  2.5,
  5,
  10,
  '+Inf',
] as const;

export type RedMetricsSummary = {
  window: 'process_lifetime';
  totalRequests: number;
  requests5xx: number;
  requests4xx: number;
  errorRate5xx: number;
  approximateP95Seconds: number;
};

/** @internal Tests and red-summary only */
export function getMetricsStoresForSummary(): {
  requestTotal: ReadonlyMap<string, number>;
  durationHistogram: ReadonlyMap<string, number[]>;
} {
  return { requestTotal, durationHistogram };
}

type RequestLabels = {
  method: string;
  route: string;
  status: string;
};

type CounterStore = Map<string, number>;
type HistogramStore = Map<string, number[]>;

const requestTotal: CounterStore = new Map();
const durationHistogram: HistogramStore = new Map();
const durationSumSec: CounterStore = new Map();

function labelsKey(labels: RequestLabels): string {
  return `${labels.method}\0${labels.route}\0${labels.status}`;
}

function parseLabelsKey(key: string): RequestLabels {
  const [method = 'UNKNOWN', route = 'unknown', status = '0'] = key.split('\0');
  return { method, route, status };
}

function _statusClass(statusCode: number): string {
  if (statusCode >= 500) return '5xx';
  if (statusCode >= 400) return '4xx';
  if (statusCode >= 300) return '3xx';
  if (statusCode >= 200) return '2xx';
  return '1xx';
}

/** Normalize paths to limit cardinality and strip PII (IDs, UUIDs). */
export function normalizeMetricPath(path: string): string {
  const withoutQuery = path.split('?')[0] ?? path;
  const normalized = withoutQuery
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, '/:id')
    .replace(/\/\d+(?=\/|$)/g, '/:id');
  return maskPiiInString(normalized);
}

function observeDuration(labels: RequestLabels, durationMs: number): void {
  const key = labelsKey(labels);
  const durationSec = durationMs / 1000;
  durationSumSec.set(key, (durationSumSec.get(key) ?? 0) + durationSec);
  let buckets = durationHistogram.get(key);
  if (!buckets) {
    buckets = new Array(DURATION_BUCKET_UPPER_SEC.length).fill(0) as number[];
    durationHistogram.set(key, buckets);
  }
  for (let i = 0; i < DURATION_BUCKET_UPPER_SEC.length; i++) {
    const upper = DURATION_BUCKET_UPPER_SEC[i];
    if (upper === undefined) break;
    if (upper === '+Inf' || durationSec <= upper) {
      buckets[i] = (buckets[i] ?? 0) + 1;
      break;
    }
  }
}

/**
 * Record one HTTP request. tenantId is accepted for future adapters but never used as a label.
 */
export function emitRequest(
  method: string,
  path: string,
  statusCode: number,
  durationMs: number,
  _tenantId?: string
): void {
  void _tenantId;
  const route = normalizeMetricPath(path);
  const labels: RequestLabels = {
    method: method.toUpperCase(),
    route,
    status: String(statusCode),
  };
  const key = labelsKey(labels);
  requestTotal.set(key, (requestTotal.get(key) ?? 0) + 1);
  observeDuration(labels, durationMs);
}

/** Prometheus text exposition format (no external deps). */
export function getMetricsSnapshot(): string {
  const lines: string[] = [];

  lines.push('# HELP http_requests_total Total HTTP requests');
  lines.push('# TYPE http_requests_total counter');
  for (const [key, value] of requestTotal.entries()) {
    const { method, route, status } = parseLabelsKey(key);
    lines.push(
      `http_requests_total{method="${escapeLabel(method)}",route="${escapeLabel(route)}",status="${escapeLabel(status)}"} ${value}`
    );
  }

  lines.push('# HELP http_request_duration_seconds Request duration histogram');
  lines.push('# TYPE http_request_duration_seconds histogram');
  for (const [key, buckets] of durationHistogram.entries()) {
    const { method, route, status } = parseLabelsKey(key);
    let cumulative = 0;
    for (let i = 0; i < DURATION_BUCKET_UPPER_SEC.length; i++) {
      cumulative += buckets[i] ?? 0;
      const le = DURATION_BUCKET_UPPER_SEC[i];
      const leValue = le === '+Inf' ? '+Inf' : String(le);
      lines.push(
        `http_request_duration_seconds_bucket{method="${escapeLabel(method)}",route="${escapeLabel(route)}",status="${escapeLabel(status)}",le="${leValue}"} ${cumulative}`
      );
    }
    lines.push(
      `http_request_duration_seconds_count{method="${escapeLabel(method)}",route="${escapeLabel(route)}",status="${escapeLabel(status)}"} ${cumulative}`
    );
    const sum = durationSumSec.get(key) ?? 0;
    lines.push(
      `http_request_duration_seconds_sum{method="${escapeLabel(method)}",route="${escapeLabel(route)}",status="${escapeLabel(status)}"} ${sum}`
    );
  }

  return lines.length > 0 ? `${lines.join('\n')}\n` : '# No metrics recorded yet\n';
}

function escapeLabel(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
}

/** Reset in-memory stores (tests only). */
export function resetMetricsForTests(): void {
  requestTotal.clear();
  durationHistogram.clear();
  durationSumSec.clear();
}
